import { Box, Button, Flex, Grid, Heading, Text, useToast } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { issbKeys, useSaveAnswer } from "hooks/query";
import { useGetQuestionnaireRounds, useGetQuestionsOfRound } from "../../../hooks/query";
import { showErrorToast } from "utils";
import { constructQueryParamsString } from "../../../utils";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { AButton } from "../../../components/A";
import { routes } from "../../../config";
import { useAuth } from "context";
import { useRightSidebar } from "../../../context/RightSidebarContext";
import useAirUserRoles from "../../../utils/AuthUtils";
import { RoundOverrideProvider } from "../../../context/RoundOverrideContext";
import FoundationSetupController from "./components/FoundationSetupController";
import questionService from "api/issb/question.service";
import fileinfoService from "api/common/fileinfo.service";
import { getFolderForRound } from "api/common/fileinfo";
import AnswerDetailManagement from "../IssbDataInput/AnswerDetailManagement";
import AnswerActionButtons from "../IssbDataInput/components/tools/AnswerActionButtons";
import IssbContainer from "../IssbContainer";
import SecondaryHeader from "../components/SecondaryHeader";
import StandardLayout from "./components/StandardLayout";
import TargetsLayout from "./components/TargetsLayout";
import OtherYearCards from "./components/OtherYearCards";
import { useRerender } from "rerenderer";
import assert from "utils/assert";
import LocalSpinner from "../../../components/LocalSpinner";
import { setAIContext, setAIEventListener, ACTION_applyAnswer } from "../../../api/ai/ai.service";

const isTargetsGroup = (questionGroup) => {
  return questionGroup?.toLowerCase().includes("target");
};

// Wrapper that forces full remount when questionId changes, resetting all state
export default function FoundationAnswerDetailPage() {
  const [searchParams] = useSearchParams();
  const questionId = searchParams.get("id");
  return <FoundationAnswerDetailPageInner key={questionId} />;
}

function FoundationAnswerDetailPageInner() {
  const { getAccessTokenSilently } = useAuth();
  const queryClient = useQueryClient();
  const { isAssigned, isOwner } = useAirUserRoles();
  const toast = useToast();
  const dispatch = useDispatch();
  const { setRightSidebar, doOpen } = useRightSidebar();
  const { rerender } = useRerender();

  const [searchParams] = useSearchParams();
  const questionId = searchParams.get("id");

  const [answerModifiedFlag, setAnswerModifiedFlag] = useState(false);
  const [fileInfoList, setFileInfoList] = useState();
  const [fileInfoLoadedForQuestion, setFileInfoLoadedForQuestion] = useState();

  // Fetch the foundation round
  const paramsString = constructQueryParamsString({ page: 0, size: 500 });
  const { data: roundsData } = useGetQuestionnaireRounds(paramsString);
  const foundationRound = roundsData?.content?.find((r) => r.foundationRound);

  // Fetch the question/answer data
  const { data: questionData } = useQuery(
    [...issbKeys.answer(), questionId],
    () => getAccessTokenSilently().then(() => questionService.get(questionId)),
    {
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0,
      enabled: !!questionId,
    }
  );

  // Use questionnaireId from question data (foundation round may not be in Redux)
  const questionnaireId = questionData?.questionnaireId || foundationRound?.id;

  // Fetch all questions of the foundation round (for other-year cards)
  // Refetch when questionId changes so sibling cards reflect latest stage
  const { data: allQuestions } = useGetQuestionsOfRound(null, questionnaireId, {
    enabled: !!questionnaireId,
    staleTime: 0,
  });

  useEffect(() => {
    if (questionnaireId) {
      queryClient.invalidateQueries(issbKeys.questionList());
    }
  }, [questionId, questionnaireId, queryClient]);

  // Set current answer
  const setCurrentAnswer = useCallback(
    (answerString) => {
      assert(!answerString || typeof answerString === "string", "setCurrentAnswer answer must be a string", answerString);
      if (questionData.answer !== answerString) {
        setAnswerModifiedFlag(true);
      }
      questionData.answer = answerString;
      rerender();

      const key = [...issbKeys.answer(), questionId];
      queryClient.setQueryData(key, (old) => {
        if (!old) return old;
        return { ...old, answer: answerString };
      });
    },
    [questionData, rerender, queryClient, questionId]
  );

  // Targets state (for Targets-type questions)
  const [targets, setTargets] = useState([]);
  const [targetsNotes, setTargetsNotes] = useState("");

  // Initialize targets from question data
  useEffect(() => {
    if (!questionData?.answer || !isTargetsGroup(questionData?.questionGroup)) return;
    try {
      const parsed = JSON.parse(questionData.answer);
      if (parsed && typeof parsed === "object") {
        setTargets(parsed.targets || []);
        setTargetsNotes(parsed.notes || "");
      }
    } catch {
      // answer is not JSON (legacy plain text), leave targets empty
    }
  }, [questionData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onTargetsChange = useCallback(
    (newTargets) => {
      setTargets(newTargets);
      const data = JSON.stringify({ targets: newTargets, notes: targetsNotes });
      questionData.answer = data;
      setAnswerModifiedFlag(true);
      rerender();
      const key = [...issbKeys.answer(), questionId];
      queryClient.setQueryData(key, (old) => (old ? { ...old, answer: data } : old));
    },
    [targetsNotes, questionData, rerender, queryClient, questionId]
  );

  const setTargetsNoteAnswer = useCallback(
    (html) => {
      setTargetsNotes(html);
      const data = JSON.stringify({ targets, notes: html });
      questionData.answer = data;
      setAnswerModifiedFlag(true);
      rerender();
      const key = [...issbKeys.answer(), questionId];
      queryClient.setQueryData(key, (old) => (old ? { ...old, answer: data } : old));
    },
    [targets, questionData, rerender, queryClient, questionId]
  );

  // Save answer
  const { mutate: saveAnswer } = useSaveAnswer({
    onMutate: () => dispatch(showAppSpinner()),
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    onSuccess: () => {
      setAnswerModifiedFlag(false);
      queryClient.invalidateQueries(issbKeys.answer());
    },
  });

  const handleSaveAnswer = useCallback(() => {
    saveAnswer({
      questionId,
      answer: questionData?.answer,
      files: questionData?.files,
    });
  }, [saveAnswer, questionId, questionData]);

  // Files
  const fileFolderForRound = getFolderForRound(questionnaireId);
  const fileInfoIds = questionData?.files;

  useEffect(() => {
    if (!fileInfoIds) return;
    if (fileInfoLoadedForQuestion === questionId) return;
    setFileInfoLoadedForQuestion(questionId);
    if (fileInfoIds.length === 0) {
      setFileInfoList([]);
      return;
    }
    const promises = fileInfoIds.map((id) => fileinfoService.get(id));
    Promise.all(promises).then((fileInfos) => {
      setFileInfoList(fileInfos);
    });
  }, [fileInfoIds, fileInfoLoadedForQuestion, questionId]);

  const onFileInfoListChange = (newFileInfoList) => {
    setFileInfoList(newFileInfoList);
    questionData.files = newFileInfoList.map((f) => f.id);
    setAnswerModifiedFlag(true);
  };

  // canSave logic (simplified — no conditions/relevance for foundation questions)
  let canSave = true;
  let cannotSaveReason;
  if (
    !["START", "PENDING", "AI_QUEUE", "EXPERT_REVIEW", "IN_PROGRESS", "EXCLUDED", "EXCLUDED_BY_RULE"].includes(
      questionData?.stageType
    )
  ) {
    if (questionData?.stageType === "INTERNAL_REVIEW" && isOwner(questionData)) {
      canSave = true;
    } else {
      canSave = false;
      cannotSaveReason = "It is in " + (questionData?.stage || "unknown") + " stage, and not in the editing stages.";
    }
  }
  if (questionData && !isAssigned(questionData)) {
    canSave = false;
    cannotSaveReason = "You are not the owner or assignee of this question.";
  }

  // Set AI context so the AI Assistant knows the current question
  useEffect(() => {
    if (!questionData) return;
    const aiContext = {
      ...questionData,
      questionId,
      questionnaireId,
    };
    setAIContext(aiContext, window.location.href);
  }, [questionId, questionData, questionnaireId]);

  // Listen for "apply answer" events from the AI chat
  const applyAIAnswerFromChat = useCallback((event) => {
    if (event.action !== ACTION_applyAnswer) return;
    if (!event.message?.content) return;
    if (event.processed) return;
    event.processed = true;
    const answer = event.message.content;
    setCurrentAnswer(answer);
    const mockQuestionData = { ...questionData, answer };
    delete mockQuestionData.assignees;
    questionService
      .update(questionId, mockQuestionData, {
        params: { draft: true, byAI: true, aiId: event.message.aiId, success: true },
      })
      .then(() => {
        if (canSave) {
          handleSaveAnswer();
        }
        queryClient.invalidateQueries(issbKeys.activities());
      });
  }, [questionData, questionId, canSave, setCurrentAnswer, handleSaveAnswer, queryClient]);

  setAIEventListener(applyAIAnswerFromChat);

  // Right sidebar
  useEffect(() => {
    if (!questionData) return;
    setRightSidebar(
      <AnswerDetailManagement questionData={questionData} canSave={canSave} hideStatuses={true} hideRelevance={true} />,
      true,
      "AnswerDetailManagement"
    );
    doOpen("AnswerDetailManagement");
  }, [canSave, doOpen, questionData, setRightSidebar]);

  const tags = useMemo(() => [], []);

  // Find previous year's targets question for import
  const previousYearTargetsQuestion = useMemo(() => {
    if (!questionData || !isTargetsGroup(questionData.questionGroup) || !allQuestions) return null;
    const currentYear = parseInt(questionData.question.match(/\d{4}/)?.[0]) || 0;
    if (!currentYear) return null;
    const sameGroup = allQuestions
      .filter((q) => isTargetsGroup(q.questionGroup) && q.id !== questionData.id)
      .map((q) => ({ ...q, year: parseInt(q.question.match(/\d{4}/)?.[0]) || 0 }))
      .filter((q) => q.year < currentYear)
      .sort((a, b) => b.year - a.year);
    return sameGroup[0] || null;
  }, [allQuestions, questionData]);

  const handleImportTargets = useCallback(async () => {
    if (!previousYearTargetsQuestion) return;
    await getAccessTokenSilently();
    const prevQuestion = await questionService.get(previousYearTargetsQuestion.id);
    if (!prevQuestion?.answer) return [];
    try {
      const parsed = JSON.parse(prevQuestion.answer);
      return parsed.targets || [];
    } catch {
      return [];
    }
  }, [previousYearTargetsQuestion, getAccessTokenSilently]);

  if (foundationRound && !(foundationRound.ownerEmails?.length > 0)) {
    return (
      <RoundOverrideProvider round={foundationRound}>
        <FoundationSetupController foundationRound={foundationRound} />
      </RoundOverrideProvider>
    );
  }

  if (!questionData) {
    return <LocalSpinner />;
  }

  const isTargets = isTargetsGroup(questionData.questionGroup);
  const isGHG = questionData.questionGroup === "GHG Emissions";
  const isRisks = questionData.questionGroup === "Risks and Opportunities";

  return (
    <RoundOverrideProvider round={foundationRound}>
    <SecondaryHeader showRoundSelector={false} showActions={false}>
    <Grid templateColumns="1fr 400px">
    <IssbContainer margin="10%">
      <Box w="100%" maxW="900px" mx="auto" p={5}>
      <Flex direction="column" gap={4}>
        {/* Back link */}
        <Flex align="center">
          <AButton
            shouldConfirm={answerModifiedFlag}
            confirmConfig={{
              title: "Unsaved changes?",
              message:
                "It looks like you are navigating away with unsaved changes. Continuing will mean these changes will be lost.",
              confirmText: "Discard Changes",
              cancelText: "Cancel",
            }}
            leftIcon={<FaArrowLeft />}
            variant="link"
            color="limeShade.600"
            href={routes.foundationData}
          >
            <Text as="span" color="whiteShade.600">
              Foundation Data
            </Text>
          </AButton>
        </Flex>

        {/* Title + description */}
        <Box>
          <Heading fontSize="heading.l" color="darkTeal" mb={4}>
            {questionData.question}
          </Heading>
          <Text fontSize="body.s" color="gray.600" mb={2}>
            {isTargets ? "Confirm sustainability targets applicable in the year" : questionData.description}
          </Text>
        </Box>

        {/* Layout by question type */}
        {isTargets ? (
          <TargetsLayout
            questionData={{ ...questionData, answer: targetsNotes }}
            canSave={canSave}
            setCurrentAnswer={setTargetsNoteAnswer}
            targets={targets}
            onTargetsChange={onTargetsChange}
            fileInfoList={fileInfoList}
            onFileInfoListChange={onFileInfoListChange}
            fileFolderForRound={fileFolderForRound}
            tags={tags}
            onImportTargets={handleImportTargets}
            hasPreviousYear={!!previousYearTargetsQuestion}
          />
        ) : (
          <StandardLayout
            questionData={questionData}
            canSave={canSave}
            setCurrentAnswer={setCurrentAnswer}
            fileInfoList={fileInfoList}
            onFileInfoListChange={onFileInfoListChange}
            fileFolderForRound={fileFolderForRound}
            tags={tags}
            showDropZone={isGHG || isRisks}
          />
        )}

        {/* Save button */}
        {canSave && (
          <Button
            variant="outline"
            w="100%"
            onClick={handleSaveAnswer}
            isDisabled={!answerModifiedFlag}
            size="lg"
          >
            Save
          </Button>
        )}

        {/* Action buttons (Submit for approval / Approve) */}
        <AnswerActionButtons
          questionData={questionData}
          questionId={questionData.id}
          currentStageType={questionData?.stageType}
          currentStageName={questionData?.stage}
          answerModifiedFlag={answerModifiedFlag}
        />

        {/* Other year cards */}
        {allQuestions && (
          <OtherYearCards currentQuestion={questionData} allQuestions={allQuestions} />
        )}
      </Flex>
      </Box>
    </IssbContainer>
    </Grid>
    </SecondaryHeader>
    </RoundOverrideProvider>
  );
}
